import { useState } from 'react'
import { useForm, useFieldArray } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Plus, Trash2 } from 'lucide-react'
import { Modal } from '@/components/ui/Modal'
import { FormField } from '@/components/ui/FormField'
import type { Client, ClientGoal } from '@/types'

const goalSchema = z.object({
  title: z.string().min(1, 'Goal title is required'),
  description: z.string().optional(),
  type: z.enum(['weight-loss', 'muscle-gain', 'strength', 'endurance', 'health', 'sport-specific', 'custom']),
  priority: z.enum(['high', 'medium', 'low']),
  deadline: z.string().optional(),
  targetValue: z.number().min(0, 'Target value must be positive').optional(),
  targetUnit: z.string().optional()
})

const editGoalsSchema = z.object({
  goals: z.array(goalSchema)
})

type EditGoalsFormData = z.infer<typeof editGoalsSchema>

interface EditGoalsModalProps {
  client: Client
  isOpen: boolean
  onClose: () => void
  onSave: (client: Client) => void
}

export function EditGoalsModal({ client, isOpen, onClose, onSave }: EditGoalsModalProps) {
  const [isLoading, setIsLoading] = useState(false)

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset
  } = useForm<EditGoalsFormData>({
    resolver: zodResolver(editGoalsSchema),
    defaultValues: {
      goals: client.goals.map(goal => ({
        title: goal.title,
        description: goal.description || '',
        type: goal.type,
        priority: goal.priority,
        deadline: goal.deadline?.toISOString().split('T')[0] || '',
        targetValue: typeof goal.target.value === 'number' ? goal.target.value : undefined,
        targetUnit: goal.target.unit || ''
      }))
    }
  })

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'goals'
  })

  const addNewGoal = () => {
    append({
      title: '',
      description: '',
      type: 'custom',
      priority: 'medium',
      deadline: '',
      targetValue: undefined,
      targetUnit: ''
    })
  }

  const handleSave = async (data: EditGoalsFormData) => {
    setIsLoading(true)
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000))
      
      const updatedGoals: ClientGoal[] = data.goals.map((goalData, index) => {
        const existingGoal = client.goals[index]
        return {
          id: existingGoal?.id || `goal-${Date.now()}-${index}`,
          title: goalData.title,
          description: goalData.description,
          type: goalData.type,
          priority: goalData.priority,
          status: existingGoal?.status || 'active',
          progress: existingGoal?.progress || 0,
          deadline: goalData.deadline ? new Date(goalData.deadline) : undefined,
          target: {
            type: 'numeric',
            value: goalData.targetValue || 0,
            unit: goalData.targetUnit,
            baseline: 0
          },
          milestones: existingGoal?.milestones || [],
          createdAt: existingGoal?.createdAt || new Date()
        }
      })
      
      const updatedClient: Client = {
        ...client,
        goals: updatedGoals,
        updatedAt: new Date()
      }
      
      onSave(updatedClient)
    } catch (error) {
      console.error('Failed to update goals:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Edit Client Goals"
      size="xl"
    >
      <form onSubmit={handleSubmit(handleSave)} className="space-y-6">
        <div className="space-y-6">
          {fields.map((field, index) => (
            <div key={field.id} className="p-4 border border-gray-200 rounded-lg">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-gray-900">Goal {index + 1}</h3>
                {fields.length > 1 && (
                  <button
                    type="button"
                    onClick={() => remove(index)}
                    className="text-danger-600 hover:text-danger-700 p-1"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  label="Goal Title"
                  htmlFor={`goals.${index}.title`}
                  required
                  error={errors.goals?.[index]?.title?.message}
                  className="md:col-span-2"
                >
                  <input
                    type="text"
                    className="coach-input"
                    placeholder="e.g., Lose 10 pounds"
                    {...register(`goals.${index}.title`)}
                  />
                </FormField>

                <FormField
                  label="Description"
                  htmlFor={`goals.${index}.description`}
                  error={errors.goals?.[index]?.description?.message}
                  className="md:col-span-2"
                >
                  <textarea
                    className="coach-input"
                    rows={2}
                    placeholder="Optional description..."
                    {...register(`goals.${index}.description`)}
                  />
                </FormField>

                <FormField
                  label="Goal Type"
                  htmlFor={`goals.${index}.type`}
                  required
                  error={errors.goals?.[index]?.type?.message}
                >
                  <select
                    className="coach-input"
                    {...register(`goals.${index}.type`)}
                  >
                    <option value="weight-loss">Weight Loss</option>
                    <option value="muscle-gain">Muscle Gain</option>
                    <option value="strength">Strength</option>
                    <option value="endurance">Endurance</option>
                    <option value="health">Health</option>
                    <option value="sport-specific">Sport Specific</option>
                    <option value="custom">Custom</option>
                  </select>
                </FormField>

                <FormField
                  label="Priority"
                  htmlFor={`goals.${index}.priority`}
                  required
                  error={errors.goals?.[index]?.priority?.message}
                >
                  <select
                    className="coach-input"
                    {...register(`goals.${index}.priority`)}
                  >
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                </FormField>

                <FormField
                  label="Target Value"
                  htmlFor={`goals.${index}.targetValue`}
                  error={errors.goals?.[index]?.targetValue?.message}
                >
                  <input
                    type="number"
                    step="0.1"
                    className="coach-input"
                    placeholder="e.g., 150"
                    {...register(`goals.${index}.targetValue`, { valueAsNumber: true })}
                  />
                </FormField>

                <FormField
                  label="Unit"
                  htmlFor={`goals.${index}.targetUnit`}
                  error={errors.goals?.[index]?.targetUnit?.message}
                >
                  <input
                    type="text"
                    className="coach-input"
                    placeholder="e.g., lbs, kg, minutes"
                    {...register(`goals.${index}.targetUnit`)}
                  />
                </FormField>

                <FormField
                  label="Target Date"
                  htmlFor={`goals.${index}.deadline`}
                  error={errors.goals?.[index]?.deadline?.message}
                  className="md:col-span-2"
                >
                  <input
                    type="date"
                    className="coach-input"
                    {...register(`goals.${index}.deadline`)}
                  />
                </FormField>
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={addNewGoal}
            className="flex items-center space-x-2 text-primary-600 hover:text-primary-700"
          >
            <Plus className="w-4 h-4" />
            <span>Add Goal</span>
          </button>

          <div className="flex items-center space-x-3">
            <button
              type="button"
              onClick={handleClose}
              className="coach-button-secondary px-4 py-2"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="coach-button-primary px-4 py-2"
              disabled={isLoading}
            >
              {isLoading ? 'Saving...' : 'Save Goals'}
            </button>
          </div>
        </div>
      </form>
    </Modal>
  )
}